import { Howl } from 'howler';
import Analytics from './analytics';

class AudioPlayer {
    constructor(playerElement) {
        this.playerElement = playerElement;
        this.hasTrackedPlay = false;
        this.progressMilestones = {
            25: false,
            50: false,
            75: false,
            100: false
        };
        this.init();
    }

    init = () => {
        const playerElement = this.playerElement;
        if (!playerElement) return;

        const audioUrl = playerElement.dataset.audioUrl;
        if (!audioUrl) return;

        const playButton = playerElement.querySelector('.audio-player__play');
        const backwardButton = playerElement.querySelector('.audio-player__backward');
        const forwardButton = playerElement.querySelector('.audio-player__forward');
        const progressBar = playerElement.querySelector('.audio-player__progress-bar');
        const progressFill = playerElement.querySelector('.audio-player__progress-fill');
        const progressHandle = playerElement.querySelector('.audio-player__progress-handle');
        const currentTimeSpan = playerElement.querySelector('.audio-player__time-current');
        const remainingTimeSpan = playerElement.querySelector('.audio-player__time-remaining');
        const statusElement = playerElement.querySelector('[role="status"]');

        this.progressAnimationFrame = null;
        this.isDragging = false;
        this.sound = null;

        const initializeSound = () => {
            if (this.sound) return;

            this.sound = new Howl({
                src: [audioUrl],
                html5: true,
                onload: () => {
                    const duration = this.sound.duration();
                    const durationFormatted = this.formatTime(duration);
                    if (remainingTimeSpan) {
                        remainingTimeSpan.textContent = `-${durationFormatted}`;
                    }
                    if (progressBar) {
                        progressBar.setAttribute('aria-valuemax', Math.floor(duration));
                    }
                    if (statusElement) {
                        statusElement.textContent = 'Ljud laddat';
                    }
                },
                onplay: () => {
                    playButton.classList.remove('audio-player__play--paused');
                    playButton.classList.add('audio-player__play--playing');
                    playButton.setAttribute('aria-label', 'Pausa ljud');
                    playButton.setAttribute('aria-pressed', 'true');
                    if (statusElement) {
                        statusElement.textContent = 'Uppspelning startad';
                    }
                    this.updateProgress();
                },
                onpause: () => {
                    playButton.classList.remove('audio-player__play--playing');
                    playButton.classList.add('audio-player__play--paused');
                    playButton.setAttribute('aria-label', 'Spela ljud');
                    playButton.setAttribute('aria-pressed', 'false');
                    if (statusElement) {
                        statusElement.textContent = 'Uppspelning pausad';
                    }
                    if (this.progressAnimationFrame) {
                        cancelAnimationFrame(this.progressAnimationFrame);
                        this.progressAnimationFrame = null;
                    }
                },
                onend: () => {
                    playButton.classList.remove('audio-player__play--playing');
                    playButton.classList.add('audio-player__play--paused');
                    playButton.setAttribute('aria-label', 'Spela ljud');
                    playButton.setAttribute('aria-pressed', 'false');
                    if (statusElement) {
                        statusElement.textContent = 'Uppspelning avslutad';
                    }
                    if (this.progressAnimationFrame) {
                        cancelAnimationFrame(this.progressAnimationFrame);
                        this.progressAnimationFrame = null;
                    }

                    if (progressFill) progressFill.style.width = '0%';
                    if (progressHandle) progressHandle.style.left = '0%';
                    if (currentTimeSpan) currentTimeSpan.textContent = '00:00';
                    if (remainingTimeSpan) {
                        const duration = this.sound.duration();
                        remainingTimeSpan.textContent = `-${this.formatTime(duration)}`;
                    }
                    if (progressBar) {
                        progressBar.setAttribute('aria-valuenow', '0');
                        progressBar.setAttribute('aria-valuetext', '0 minuter 0 sekunder');
                    }
                }
            });
        };

        this.updateProgress = () => {
            if (this.sound && this.sound.playing() && !this.isDragging) {
                const seek = this.sound.seek() || 0;
                const duration = this.sound.duration();
                const progress = (seek / duration) * 100;
                const remaining = duration - seek;

                if (progressFill) {
                    progressFill.style.width = `${progress}%`;
                }
                if (progressHandle) {
                    progressHandle.style.left = `${progress}%`;
                }
                if (currentTimeSpan) {
                    currentTimeSpan.textContent = this.formatTime(seek);
                }
                if (remainingTimeSpan) {
                    remainingTimeSpan.textContent = `-${this.formatTime(remaining)}`;
                }
                if (progressBar) {
                    progressBar.setAttribute('aria-valuenow', Math.floor(seek));
                    progressBar.setAttribute('aria-valuetext', `${this.formatTime(seek)} av ${this.formatTime(duration)}`);
                }

                this.trackProgressMilestones(progress);

                this.progressAnimationFrame = requestAnimationFrame(this.updateProgress);
            }
        };

        const updateAriaValues = (currentTime, duration) => {
            if (progressBar) {
                progressBar.setAttribute('aria-valuenow', Math.floor(currentTime));
                progressBar.setAttribute('aria-valuetext', `${this.formatTime(currentTime)} av ${this.formatTime(duration)}`);
            }
        };

        if (playButton) {
            playButton.addEventListener('click', () => {
                initializeSound();
                if (this.sound.playing()) {
                    this.sound.pause();
                } else {
                    this.sound.play();

                    if (!this.hasTrackedPlay) {
                        this.hasTrackedPlay = true;
                        const headline = playerElement.dataset.headline || '';

                        if (window.dataLayer) {
                            window.dataLayer.push({
                                'event': 'playAiPod',
                                'aipodHeadline': headline,
                                'isAuthenticated': Analytics.getAuthenticated(),
                                'selectedBranch': Analytics.getBranchName()
                            });
                        }
                    }
                }
            });
        }

        if (backwardButton) {
            backwardButton.addEventListener('click', () => {
                if (!this.sound) return;
                const wasPlaying = this.sound.playing();
                const currentSeek = this.sound.seek() || 0;
                const newSeek = Math.max(0, currentSeek - 15);
                this.sound.seek(newSeek);

                if (wasPlaying && !this.sound.playing()) {
                    this.sound.play();
                }

                const duration = this.sound.duration();
                if (progressFill && progressHandle) {
                    const progress = (newSeek / duration) * 100;
                    progressFill.style.width = `${progress}%`;
                    progressHandle.style.left = `${progress}%`;
                }
                if (currentTimeSpan) {
                    currentTimeSpan.textContent = this.formatTime(newSeek);
                }
                if (remainingTimeSpan) {
                    remainingTimeSpan.textContent = `-${this.formatTime(duration - newSeek)}`;
                }
                updateAriaValues(newSeek, duration);
                if (statusElement) {
                    statusElement.textContent = 'Spolade bakåt 15 sekunder';
                }
            });
        }

        if (forwardButton) {
            forwardButton.addEventListener('click', () => {
                if (!this.sound) return;
                const wasPlaying = this.sound.playing();
                const currentSeek = this.sound.seek() || 0;
                const duration = this.sound.duration();
                const newSeek = Math.min(duration, currentSeek + 15);
                this.sound.seek(newSeek);

                if (wasPlaying && !this.sound.playing()) {
                    this.sound.play();
                }

                if (progressFill && progressHandle) {
                    const progress = (newSeek / duration) * 100;
                    progressFill.style.width = `${progress}%`;
                    progressHandle.style.left = `${progress}%`;
                }
                if (currentTimeSpan) {
                    currentTimeSpan.textContent = this.formatTime(newSeek);
                }
                if (remainingTimeSpan) {
                    remainingTimeSpan.textContent = `-${this.formatTime(duration - newSeek)}`;
                }
                updateAriaValues(newSeek, duration);
                if (statusElement) {
                    statusElement.textContent = 'Spolade framåt 15 sekunder';
                }
            });
        }

        if (progressBar) {
            progressBar.addEventListener('keydown', (e) => {
                if (!this.sound) return;
                const duration = this.sound.duration();
                const currentSeek = this.sound.seek() || 0;
                let newSeek = currentSeek;
                let handled = false;

                switch(e.key) {
                    case 'ArrowRight':
                    case 'ArrowUp':
                        e.preventDefault();
                        newSeek = Math.min(duration, currentSeek + 5);
                        handled = true;
                        if (statusElement) {
                            statusElement.textContent = 'Spolade framåt 5 sekunder';
                        }
                        break;
                    case 'ArrowLeft':
                    case 'ArrowDown':
                        e.preventDefault();
                        newSeek = Math.max(0, currentSeek - 5);
                        handled = true;
                        if (statusElement) {
                            statusElement.textContent = 'Spolade bakåt 5 sekunder';
                        }
                        break;
                    case 'Home':
                        e.preventDefault();
                        newSeek = 0;
                        handled = true;
                        if (statusElement) {
                            statusElement.textContent = 'Spolade till början';
                        }
                        break;
                    case 'End':
                        e.preventDefault();
                        newSeek = duration;
                        handled = true;
                        if (statusElement) {
                            statusElement.textContent = 'Spolade till slutet';
                        }
                        break;
                    case 'PageUp':
                        e.preventDefault();
                        newSeek = Math.min(duration, currentSeek + 30);
                        handled = true;
                        if (statusElement) {
                            statusElement.textContent = 'Spolade framåt 30 sekunder';
                        }
                        break;
                    case 'PageDown':
                        e.preventDefault();
                        newSeek = Math.max(0, currentSeek - 30);
                        handled = true;
                        if (statusElement) {
                            statusElement.textContent = 'Spolade bakåt 30 sekunder';
                        }
                        break;
                    default:
                        return;
                }

                if (handled) {
                    const wasPlaying = this.sound.playing();
                    this.sound.seek(newSeek);

                    if (wasPlaying && !this.sound.playing()) {
                        this.sound.play();
                    }

                    const progress = (newSeek / duration) * 100;
                    if (progressFill) {
                        progressFill.style.width = `${progress}%`;
                    }
                    if (progressHandle) {
                        progressHandle.style.left = `${progress}%`;
                    }
                    if (currentTimeSpan) {
                        currentTimeSpan.textContent = this.formatTime(newSeek);
                    }
                    if (remainingTimeSpan) {
                        remainingTimeSpan.textContent = `-${this.formatTime(duration - newSeek)}`;
                    }
                    updateAriaValues(newSeek, duration);
                }
            });
        }

        const updateSeekPosition = (clientX) => {
            if (!this.sound) return;
            const rect = progressBar.getBoundingClientRect();
            const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
            const duration = this.sound.duration();
            const seekTime = duration * percent;

            const wasPlaying = this.sound.playing();
            this.sound.seek(seekTime);

            if (progressFill) {
                progressFill.style.width = `${percent * 100}%`;
            }
            if (progressHandle) {
                progressHandle.style.left = `${percent * 100}%`;
            }
            if (currentTimeSpan) {
                currentTimeSpan.textContent = this.formatTime(seekTime);
            }
            if (remainingTimeSpan) {
                const remaining = duration - seekTime;
                remainingTimeSpan.textContent = `-${this.formatTime(remaining)}`;
            }
            updateAriaValues(seekTime, duration);

            if (wasPlaying && !this.sound.playing()) {
                this.sound.play();
            }
        };

        if (progressBar) {
            progressBar.addEventListener('click', (e) => {
                if (!this.isDragging) {
                    if (this.progressAnimationFrame) {
                        cancelAnimationFrame(this.progressAnimationFrame);
                        this.progressAnimationFrame = null;
                    }

                    updateSeekPosition(e.clientX);

                    if (this.sound && this.sound.playing()) {
                        setTimeout(() => {
                            this.updateProgress();
                        }, 10);
                    }
                }
            });
        }

        if (progressHandle && progressBar) {
            progressHandle.addEventListener('mousedown', (e) => {
                this.isDragging = true;
                e.preventDefault();

                const handleMouseMove = (e) => {
                    if (this.isDragging) {
                        updateSeekPosition(e.clientX);
                    }
                };

                const handleMouseUp = () => {
                    this.isDragging = false;
                    document.removeEventListener('mousemove', handleMouseMove);
                    document.removeEventListener('mouseup', handleMouseUp);

                    if (this.sound && this.sound.playing()) {
                        if (this.progressAnimationFrame) {
                            cancelAnimationFrame(this.progressAnimationFrame);
                            this.progressAnimationFrame = null;
                        }
                        setTimeout(() => {
                            this.updateProgress();
                        }, 10);
                    }
                };

                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);
            });

            progressHandle.addEventListener('touchstart', (e) => {
                this.isDragging = true;
                e.preventDefault();

                const handleTouchMove = (e) => {
                    if (this.isDragging) {
                        const touch = e.touches[0];
                        updateSeekPosition(touch.clientX);
                    }
                };

                const handleTouchEnd = () => {
                    this.isDragging = false;
                    document.removeEventListener('touchmove', handleTouchMove);
                    document.removeEventListener('touchend', handleTouchEnd);

                    if (this.sound && this.sound.playing()) {
                        if (this.progressAnimationFrame) {
                            cancelAnimationFrame(this.progressAnimationFrame);
                            this.progressAnimationFrame = null;
                        }

                        setTimeout(() => {
                            this.updateProgress();
                        }, 10);
                    }
                };

                document.addEventListener('touchmove', handleTouchMove);
                document.addEventListener('touchend', handleTouchEnd);
            });
        }
    }

    trackProgressMilestones(progress) {
        [25, 50, 75, 100].forEach(milestone => {
            const threshold = milestone === 100 ? 95 : milestone;
            if (progress >= threshold && !this.progressMilestones[milestone]) {
                this.progressMilestones[milestone] = true;

                if (window.dataLayer) {
                    const headline = this.playerElement.dataset.headline || '';

                    window.dataLayer.push({
                        'event': 'progressAiPod',
                        'aipodHeadline': headline,
                        'aipodPercent': milestone.toString(),
                        'isAuthenticated': Analytics.getAuthenticated(),
                        'selectedBranch': Analytics.getBranchName()
                    });
                }
            }
        });
    }

    formatTime(seconds) {
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
}

class AudioPlayerManager {
    constructor() {
        this.initializedPlayers = new WeakSet();
        this.initializePlayers();
        this.observeNewPlayers();
    }

    initializePlayers() {
        const playerElements = document.querySelectorAll('.audio-player');
        playerElements.forEach(playerElement => {
            if (!this.initializedPlayers.has(playerElement)) {
                new AudioPlayer(playerElement);
                this.initializedPlayers.add(playerElement);
            }
        });
    }

    observeNewPlayers() {
        const observer = new MutationObserver((mutations) => {
            let hasNewPlayers = false;
            
            mutations.forEach(mutation => {
                mutation.addedNodes.forEach(node => {
                    if (node.nodeType === 1) {
                        if (node.classList && node.classList.contains('audio-player')) {
                            hasNewPlayers = true;
                        } else if (node.querySelectorAll) {
                            const players = node.querySelectorAll('.audio-player');
                            if (players.length > 0) {
                                hasNewPlayers = true;
                            }
                        }
                    }
                });
            });

            if (hasNewPlayers) {
                this.initializePlayers();
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }
}

document.addEventListener("DOMContentLoaded", function () {
    new AudioPlayerManager();
})